import { createAsyncThunk } from '@reduxjs/toolkit';

import instance from 'utils/API';

import { GeneralStepConversationTopics, SharePermissions, TicketStages } from 'core/enums/common';

import { ISharePermissionEdit } from 'components/TicketShare/CreateTicketShareModal';

export const getTicketShares = createAsyncThunk(
	'ticketShare/getTicketShares',
	async (
		{
			ticketId,
		}: {
			ticketId: string;
		},
		{ rejectWithValue }
	) => {
		try {
			const response = await instance.post(
				`/TicketShare/getListOfShares`,
				{
					currentPageNumber: 1,
					recordsPerPage: 16,
				},
				{
					params: {
						ticketId: ticketId,
					},
				}
			);

			const { data } = response.data;

			return data;
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const revokeTicketSharePermission = createAsyncThunk(
	'ticketShare/revokePermission',
	async (
		{
			shareIds,
		}: {
			shareIds: Array<string>;
		},
		{ rejectWithValue }
	) => {
		try {
			await instance.delete(`/TicketShare/deleteManyTicketShare`, {
				data: shareIds,
			});

			return { shareIds: shareIds };
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const updateTicketSharePermission = createAsyncThunk(
	'ticketShare/updatePermission',
	async (
		{
			editedPermission,
		}: {
			editedPermission: ISharePermissionEdit;
		},
		{ rejectWithValue }
	) => {
		try {
			await instance.put(
				`/TicketShare/updateTicketShare`,
				{
					permission: editedPermission.permission,
					revokedAt: null,
				},
				{
					params: {
						ticketShareId: editedPermission.shareId,
					},
				}
			);

			return { ticketShareId: editedPermission.shareId, permission: editedPermission.permission };
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const createTicketShare = createAsyncThunk(
	'ticketShare/createTicketShare',
	async (
		{
			shareUserId,
			ticketId,
			permission,
			ticketCurrentStep,
			subStageEnum,
		}: {
			shareUserId: string;
			ticketId: string;
			permission: SharePermissions;
			ticketCurrentStep: Array<TicketStages> | null;
			subStageEnum: Array<GeneralStepConversationTopics> | null;
		},
		{ rejectWithValue }
	) => {
		try {
			// if all stages selected. send single request
			if (ticketCurrentStep === null) {
				const response = await instance.post(`/TicketShare/createTicketShare`, {
					sharedUserId: shareUserId,
					revokedAt: null,
					ticketId: ticketId,
					permission: permission,
					ticketCurrentStep: ticketCurrentStep,
					subStageEnum: null,
				});

				const { data } = response.data;

				return data;
			} else {
				// If stage is general, create permission for each subStage
				const multipleTicketSubStageSharesPermission =
					subStageEnum === null
						? {
								ticketCurrentStep: TicketStages.general,
								subStageEnum: null,
						  }
						: subStageEnum.map((subStage) => ({
								ticketCurrentStep: TicketStages.general,
								subStageEnum: subStage,
						  }));

				// parse multiple shares to permissions array.
				const multipleTicketSharesPermission = ticketCurrentStep.map((stage) =>
					stage === TicketStages.general
						? multipleTicketSubStageSharesPermission
						: {
								ticketCurrentStep: stage,
								subStageEnum: null,
						  }
				);

				// if subStages are edited. General stage may not be in currentTicketStep
				if (
					(subStageEnum?.length || subStageEnum === null) &&
					!ticketCurrentStep?.includes(TicketStages.general)
				) {
					multipleTicketSharesPermission.push(multipleTicketSubStageSharesPermission);
				}

				const multipleRequestsBody = {
					sharedUserId: shareUserId,
					revokedAt: null,
					ticketId: ticketId,
					permission: permission,
					createTicketShareDtos: multipleTicketSharesPermission.flat(),
				};

				const response = await instance.post(
					`/TicketShare/createManyTicketShares`,
					multipleRequestsBody
				);

				const { data } = response.data;

				return data;
			}
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);
